import React, { FunctionComponent, MouseEvent, useState, useEffect } from 'react';
import { KeyboardArrowDown, KeyboardArrowUp, MailOutline } from '@mui/icons-material';
import {
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import useTypedSelector from 'hooks/useTypedSelector';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { userSelector } from 'redux/selectors/auth.selector';

import { INavbarProps } from './types';
import {
  NavbarContainer,
  NavbarContent,
  NavbarMenuButton,
  NavbarAccountButton,
  NavbarProfileAvatar,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuItemLabel,
  NavbarNavlinkContainer,
  NavbarNavlinkButton,
  NavbarLogoutButton,
  NavbarHelpButton,
  WhatsAppMenuIcon,
} from './styles';
import './i18n';

const Navbar: FunctionComponent<INavbarProps> = (props: INavbarProps) => {
  const {
    initials,
    isMobile,
    isPublic,
    items,
    onLogoutPublicClick,
    onOpenMenu,
    title,
    isVerified,
    onTitleClick,
  } = props;

  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    if (!menuElement) return;
    const getScrollbarWidth = () => {
      const { documentElement } = document;
      const hasScroll = documentElement.scrollHeight > documentElement.clientHeight;
      setScrollbarWidth(hasScroll ? window.innerWidth - documentElement.clientWidth : 0);
    };
    getScrollbarWidth();
    window.addEventListener('resize', getScrollbarWidth);
    // eslint-disable-next-line consistent-return -- cleanup for useEffect
    return () => window.removeEventListener('resize', getScrollbarWidth);
  }, [menuElement]);

  const whatsappNumber = useTypedSelector((state) => state.contactSettings?.item?.whatsappNumber);

  const handleHelpClick = (event: MouseEvent<HTMLElement>) => setHelpAnchor(event.currentTarget);
  const handleHelpClose = () => setHelpAnchor(null);

  const user = useTypedSelector(userSelector);
  const isLoggedIn = !!user;

  const openWhatsApp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=Hola, necesito ayuda con un trámite`,
      '_blank',
    );
    handleHelpClose();
  };

  const onCloseAccountMenu = () => setMenuElement(null);

  const openEmail = () => {
    history.push('/public/contact');
    handleHelpClose();
    onCloseAccountMenu();
  };

  const renderPublicButtons = () => (
    <>
      {isLoggedIn && (
        <>
          <NavbarNavlinkButton
            activeClassName="active"
            onClick={onCloseAccountMenu}
            to="/public/procedures/forms"
          >
            {i18next.t<string>('NAVBAR:PROCEDURES')}
          </NavbarNavlinkButton>
          <NavbarNavlinkButton
            activeClassName="active"
            onClick={onCloseAccountMenu}
            to="/public/procedures/list"
          >
            {i18next.t<string>('NAVBAR:MY_PROCEDURES')}
          </NavbarNavlinkButton>
        </>
      )}
      {/* <NavbarNavlinkButton
        activeClassName="active"
        onClick={onCloseAccountMenu}
        to="/public/procedures/contact"
      >
        {i18next.t<string>('NAVBAR:CONTACT')}
      </NavbarNavlinkButton> */}
      <Tooltip title={i18next.t<string>('NAVBAR:HELP_TOOLTIP_TITLE')}>
        <NavbarHelpButton onClick={handleHelpClick}>
          {i18next.t<string>('NAVBAR:HELP_TOOLTIP', 'Contacto')}
        </NavbarHelpButton>
      </Tooltip>
      <Menu
        anchorEl={helpAnchor}
        open={Boolean(helpAnchor)}
        onClose={handleHelpClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={openWhatsApp}>
          <ListItemIcon>
            <WhatsAppMenuIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="WhatsApp" />
        </MenuItem>
        <MenuItem onClick={openEmail}>
          <ListItemIcon>
            <MailOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={i18next.t<string>('NAVBAR:SEND_EMAIL', 'Enviar Correo')} />
        </MenuItem>
      </Menu>
      {isLoggedIn ? (
        <NavbarLogoutButton onClick={onLogoutPublicClick}>
          {i18next.t<string>('NAVBAR:LOG_OUT')}
        </NavbarLogoutButton>
      ) : (
        <NavbarLogoutButton onClick={() => history.push('/public/procedures/login')}>
          {i18next.t<string>('NAVBAR:LOGIN', 'Iniciar sesión')}
        </NavbarLogoutButton>
      )}
    </>
  );

  return (
    <NavbarContainer auth data-testid="navbar">
      {isPublic && !isVerified && (
        <Alert
          severity="warning"
          variant="filled"
          square
          sx={{ width: '100%', py: 0.5, fontSize: '0.85rem' }}
        >
          {i18next.t<string>('NAVBAR:VERIFICATION_REQUIRED')}
        </Alert>
      )}
      <NavbarContent auth isPublic={isPublic} style={{ paddingLeft: '24px', paddingRight: '24px' }}>
        {!isPublic && <NavbarMenuButton onClick={onOpenMenu} />}
        {/* Título a la izquierda */}
        <Typography
          variant="h6"
          onClick={onTitleClick}
          sx={{
            cursor: onTitleClick ? 'pointer' : 'default',
            userSelect: 'none',
            fontSize: '1.1rem',
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        {/* Botones a la derecha */}
        {isPublic &&
          (!isMobile ? (
            <NavbarNavlinkContainer>{renderPublicButtons()}</NavbarNavlinkContainer>
          ) : (
            <>
              <NavbarAccountButton
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  setMenuElement(event.currentTarget)
                }
                isPublic
              >
                {i18next.t<string>('NAVBAR:MENU', 'Menú')}{' '}
                {menuElement ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </NavbarAccountButton>
              <NavbarMenu
                anchorEl={menuElement}
                onClose={onCloseAccountMenu}
                open={!!menuElement}
                isPublic
                scrollbarWidth={scrollbarWidth}
              >
                {renderPublicButtons()}
              </NavbarMenu>
            </>
          ))}
        {/* Avatar menú para usuarios autenticados */}
        {!isPublic && (
          <>
            <NavbarAccountButton
              onClick={(event: MouseEvent<HTMLButtonElement>) =>
                setMenuElement(event.currentTarget)
              }
            >
              <NavbarProfileAvatar alt={initials}>{initials}</NavbarProfileAvatar>
            </NavbarAccountButton>
            <NavbarMenu
              anchorEl={menuElement}
              onClose={onCloseAccountMenu}
              open={!!menuElement}
              scrollbarWidth={scrollbarWidth}
            >
              {items.map(({ Icon, key, label, onClick }) => (
                <NavbarMenuItem
                  key={key}
                  onClick={() => {
                    onClick();
                    onCloseAccountMenu();
                  }}
                >
                  {Icon && <Icon fontSize="small" sx={{ mr: 1 }} />}
                  <NavbarMenuItemLabel>{label}</NavbarMenuItemLabel>
                </NavbarMenuItem>
              ))}
            </NavbarMenu>
          </>
        )}
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar;
